import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import contactEmptyMascot from '../../../../assets/contact-empty-mascot.png';
import Icon from '../../../../components/Icon/Icon';
import { whatsappBillingAPI } from '../../../../helpers/whatsappApiHandler';
import { showAlert } from '../../../../store/actions/alertAction';
import WhatsAppBillingDataTable from './WhatsAppBillingDataTable';
import WhatsAppBillingFilters from './WhatsAppBillingFilters';

const WhatsAppBillingTabContent = ({ wabaId, phoneNumberId, phoneDetails }) => {
  const [billingData, setBillingData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    country: ['MY'],
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
  });

  // Get access token from Redux store
  const accessToken = useSelector((state) => state.auth.access_token?.token);
  const dispatch = useDispatch();

  const loadBillingData = useCallback(async () => {
    setLoadingData(true);
    setError('');

    const cleanPhoneNumber = phoneDetails.display_phone_number?.replace(/[+\-\s]/g, '');

    try {
      const params = {
        start: Math.floor(new Date(filters.startDate).getTime() / 1000),
        end: Math.floor(new Date(filters.endDate).getTime() / 1000),
        granularity: 'DAILY',
        country_codes: Array.isArray(filters.country) ? filters.country.join(',') : filters.country,
      };

      const response = await whatsappBillingAPI.getBillingAnalytics(
        wabaId,
        cleanPhoneNumber,
        params,
        accessToken
      );

      console.log(response.data);

      if (response.success) {
        const hasData = response.data.data?.countries?.length > 0;
        setBillingData(response.data.data);
        // Clear any previous errors

        // Show alert if no data is available
        if (!hasData) {
          dispatch(showAlert('No billing data available for the selected filters.', 'warning'));
        }
      } else {
        dispatch(showAlert('Failed to load billing data', 'warning'));
        setBillingData(null);
      }
    } catch (err) {
      console.error('Error loading billing data:', err);
    } finally {
      setLoadingData(false);
    }
  }, [accessToken, wabaId, phoneNumberId, filters, dispatch]);

  // Load billing data on component mount and filter changes
  useEffect(() => {
    if (accessToken && wabaId && phoneNumberId) {
      loadBillingData();
    }
  }, [accessToken, wabaId, phoneNumberId, loadBillingData]);

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleExport = () => {
    try {
      if (!billingData || !billingData.countries || billingData.countries.length === 0) {
        setError('No billing data available to export');
        setTimeout(() => setError(''), 5000);
        return;
      }

      // Convert billing data to CSV format
      const csvRows = [];

      // Add CSV headers
      csvRows.push(
        [
          'Country',
          'Country Code',
          'Category',
          'Volume',
          'Cost',
          'Cost Per Message',
        ].join(',')
      );

      // Process each country's data (new format from local billing calculation)
      billingData.countries.forEach((country) => {
        country.conversation_types?.forEach((type) => {
          const row = [
            country.country_name || 'N/A',
            country.country_code || 'N/A',
            type.category || 'N/A',
            type.volume || 0,
            type.cost?.toFixed(4) || '0.0000',
            type.cost_per_message?.toFixed(4) || '0.0000',
          ];
          csvRows.push(row.join(','));
        });
      });

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `whatsapp-billing-${filters.startDate}-to-${filters.endDate}.csv`
      );
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      setSuccess('Billing data exported successfully!');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error exporting billing data:', err);
      setError('Failed to export billing data');
      setTimeout(() => setError(''), 5000);
    }
  };

  const renderBillingHeader = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignSelf: 'stretch',
        gap: '20px',
        padding: '20px 32px',
        borderRadius: '0px 20px 0px 0px',
        backgroundColor: '#FFFFFF',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          alignSelf: 'stretch',
          gap: '10px',
        }}
      >
        {/* Left side - Country, Date, Apply Filter */}
        <WhatsAppBillingFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onApplyFilter={loadBillingData}
          loading={loadingData}
        />

        {/* Right side - Export Button */}
        <Button
          onClick={handleExport}
          disabled={loadingData || !billingData || billingData.countries?.length === 0}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 18px',
            backgroundColor:
              loadingData || !billingData || billingData.countries?.length === 0
                ? '#BDC3C7'
                : '#0BA9E0',
            border: 'none',
            borderRadius: '4px',
            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
            fontFamily: 'Poppins',
            fontWeight: '500',
            fontSize: '13px',
            lineHeight: '1.5em',
            color: '#FFFFFF',
          }}
        >
          <Icon name="upload" color="#FFFFFF" width={17} height={17} />
          Export
        </Button>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: '20px',
        padding: '24px 32px',
        minHeight: '669px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '30px 0px',
          gap: '20px',
        }}
      >
        <img
          src={contactEmptyMascot}
          alt="No billing data"
          style={{
            width: '215px',
            height: '215px',
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            width: '544px',
          }}
        >
          <h3
            style={{
              fontFamily: 'Poppins',
              fontWeight: '600',
              fontSize: '18px',
              lineHeight: '1.5em',
              color: '#344054',
              margin: '0',
              textAlign: 'center',
            }}
          >
            Welcome to your Billings page!
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              width: '555px',
            }}
          >
            <p
              style={{
                fontFamily: 'Poppins',
                fontWeight: '400',
                fontSize: '14px',
                lineHeight: '1.5em',
                color: '#344054',
                margin: '0',
                textAlign: 'center',
                width: '554px',
              }}
            >
              Get better visibility into your WhatsApp Business account activity from detailed
              breakdown of your message usage and estimated charges.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Show loading state
  if (loadingData && !billingData) {
    return (
      <div
        style={{
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          borderRadius: '0px 20px 20px 20px',
          margin: '0 40px 40px 40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading billing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        borderRadius: '0px 20px 20px 20px',
        margin: '0 40px 40px 40px',
      }}
    >
      <div style={{ padding: '0' }}>
        {renderBillingHeader()}

        {/* Success/Error Messages */}
        {success && (
          <div style={{ padding: '0 32px 16px 32px' }}>
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </div>
        )}

        {error && (
          <div style={{ padding: '0 32px 16px 32px' }}>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <strong>Error:</strong> {error}
              <Button variant="outline-danger" size="sm" className="ms-2" onClick={loadBillingData}>
                Retry
              </Button>
            </Alert>
          </div>
        )}

        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '0px 0px 20px 20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            gap: '20px',
          }}
        >
          {billingData?.countries?.length > 0 ? (
            <WhatsAppBillingDataTable
              data={billingData}
              loadingData={loadingData}
              onRefresh={loadBillingData}
            />
          ) : (
            renderEmptyState()
          )}
        </div>
      </div>
    </div>
  );
};

// Helper functions
function getDefaultStartDate() {
  const date = new Date();
  date.setDate(date.getDate() - 30); // 30 days ago
  return date.toISOString().split('T')[0];
}

function getDefaultEndDate() {
  const date = new Date();
  return date.toISOString().split('T')[0];
}

export default WhatsAppBillingTabContent;
